import type { FC } from "react";
import { useState } from "react";

import classNames from "classnames";

import { ComponentHeading } from "@jti/ui/components/ComponentHeading";
import { DetailsBar } from "@jti/ui/components/DetailsBar";
import { LoadMore } from "@jti/ui/components/LoadMore";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { scrollToElementWithRetry } from "@jti/ui/utils/scrollToElementWithRetry";

import styles from "./EarnPoints.module.scss";

export type EarnPointItemActionType = "button" | "text";
export type EarnPointItemProps = {
  pointsCount: string;
  pointsTitle: string;
  pointsDescription: string;
  pointsActionText: string;
  pointsLinkUrl?: string;
  pointsActionTypeRadio: EarnPointItemActionType;
};

export interface EarnPointsProps {
  header: string;
  title: string;
  details: string;
  subtitle: string;
  subtitleDetails: string;
  loadBaseItems: number;
  pointsList: EarnPointItemProps[];
  pointsLabel: string;
  componentId?: string;
  loadMoreItems: number;
  theme?: "light" | "dark";
}
const EARN_POINTS_BLOCK_ID = "earnPointsBlock";

export const EarnPoints: FC<EarnPointsProps> = ({
  header,
  title,
  details,
  subtitle,
  subtitleDetails,
  pointsList = [],
  pointsLabel,
  loadBaseItems,
  componentId,
  loadMoreItems,
  theme,
}) => {
  const [numberOfItemsToShow, setNumberOfItemsToShow] = useState<number>(loadBaseItems);
  const itemsToShow = pointsList.slice(0, numberOfItemsToShow);
  const showLoadMore = itemsToShow.length < pointsList.length;
  const { __ } = useTranslations();
  const isCollapsable = pointsList.length > loadBaseItems;

  const handleShowMoreItems = () => {
    if (showLoadMore) {
      setNumberOfItemsToShow((prevValue) => prevValue + loadMoreItems);
    } else {
      scrollToElementWithRetry(EARN_POINTS_BLOCK_ID);
      setNumberOfItemsToShow(loadBaseItems);
    }
  };

  return (
    <div
      className={classNames(styles.block, {
        ["light-theme"]: theme === "light",
        ["dark-theme"]: theme === "dark",
      })}
      id={componentId}
      data-testid="earnPointsBlock"
    >
      <div className={styles.holder}>
        <ComponentHeading>{header}</ComponentHeading>
        <div className={styles.outline}>
          <div className={styles.infoBlock}>
            <div className={styles.textSection}>
              <div className={styles.title}>{title}</div>
              <div className={styles.details}>{details}</div>
            </div>
            <div className={styles.textSection}>
              <div className={styles.subtitle}>{subtitle}</div>
              <div className={styles.details}>{subtitleDetails}</div>
            </div>
          </div>
          <div id={EARN_POINTS_BLOCK_ID} className={styles.pointsBlock}>
            {itemsToShow.map(
              ({
                pointsTitle,
                pointsCount,
                pointsDescription,
                pointsActionText,
                pointsActionTypeRadio,
                pointsLinkUrl,
              }) => (
                <DetailsBar
                  key={pointsTitle}
                  leftItem={pointsCount}
                  leftDescription={pointsLabel}
                  rightTitle={pointsTitle}
                  buttonType={pointsActionTypeRadio}
                  rightDescription={pointsDescription}
                  buttonLabel={pointsActionText}
                  buttonUrl={pointsLinkUrl}
                />
              )
            )}
          </div>
        </div>
      </div>
      {isCollapsable && (
        <LoadMore
          label={__(showLoadMore ? "loyalty.load.more.label" : "loyalty.load.less.label")}
          expanded={!showLoadMore}
          onClick={handleShowMoreItems}
          data-testid="loadMoreLess"
        />
      )}
    </div>
  );
};
